import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { capitalize } from 'lodash';
import { formatPrice } from '@format/currency';
import { IconButton, Button } from '@ui/button';
import { VisuallyHidden, ButtonLink } from '@web/atoms';
import { useShopifyCart } from '@web/shopify';
import { ResponsiveImage, List, BaseHeader } from '@web/molecules';
import { userViewMealPlanTypeScreen } from '@web/account/MealPrepTypeSelectionForm/events';
import { CheckoutBar } from '../CheckoutBar';
import { RecommendedMealPlanForm } from '../RecommendedPlanForm';
import { useReporter } from '../../reporter';
import { onboardingBackArrowClicked } from '../events';
import { userContinuesFromRecommendedPlan } from './events';
import {
  PlanInformation,
  RecommendedPlanContainer,
  StyledRecommendedContainer,
  MobileHeader,
  DesktopHeader,
  PupSummary,
  SummaryHeadline,
  ImageContainer,
  StarRating,
  MobileButtonContainer,
  DesktopButtonContainer,
  RecipesText,
  ArrowContainer,
  Price,
  StrikeThroughPrice,
  DisplayedPrice,
  SavePercentage,
} from './styles';

const PricingInfo = ({ price, discountedPrice, saveText, period }) => (
  <Price>
    {parseFloat(discountedPrice) < parseFloat(price) ? (
      <>
        <StrikeThroughPrice>{formatPrice(price)}</StrikeThroughPrice>
        <DisplayedPrice>
          {formatPrice(discountedPrice)}/{period}
        </DisplayedPrice>
        {Boolean(saveText) && <SavePercentage>{saveText}</SavePercentage>}
      </>
    ) : (
      formatPrice(price)
    )}
  </Price>
);

export const RecommendedPlanFormOptimized = ({
  onRecommendedSubmit,
  headline,
  onBackButtonClick,
  reviewsCopy,
  savePercentText,
  defaultRecipes,
  getConfigFromRecipes,
  planImage,
  pupInfo,
  ctaButtonText = 'Continue',
  mealPlanProps,
  personalizePlanText = 'Choose your own plan instead',
  period,
}) => {
  const { cart } = useShopifyCart();
  const viewPageTagFired = useRef(false);
  const selectedRecipes = defaultRecipes;
  const { price, discountedPrice } = getConfigFromRecipes(
    'fresh',
    defaultRecipes,
  );
  const personalizedPlan = useRef(false);
  const [showRecommendedPlan, setShowRecommendedPlan] = useState(true);
  const reporter = useReporter();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const goBackWithReporter = () => {
    reporter.tag(
      onboardingBackArrowClicked('recommended_plan', cart?.customer),
    );
    onBackButtonClick();
  };
  useEffect(() => {
    if (!viewPageTagFired.current) {
      reporter.tag(userViewMealPlanTypeScreen());
      viewPageTagFired.current = true;
    }
  }, [defaultRecipes, reporter, cart]);

  const isValid = selectedRecipes && selectedRecipes.length > 0;

  const onSubmitHandler = async event => {
    event.preventDefault();
    if (!isValid) {
      return;
    }
    const customerEmail = cart?.customer?.email;
    reporter.tag(
      userContinuesFromRecommendedPlan(customerEmail, 'fresh', selectedRecipes),
    );
    setIsSubmitting(true);
    await onRecommendedSubmit(selectedRecipes);
  };
  const checkoutBarProps = {
    ctaText: ctaButtonText,
    isSubmitDisabled: !isValid || isSubmitting,
    isBackButtonDisabled: isSubmitting,
    shown: true,
    isSubmitting,
    buttonVariant: 'sea2',
  };

  const goBackToRecommended = () => {
    setShowRecommendedPlan(true);
  };

  return (
    <StyledRecommendedContainer>
      {showRecommendedPlan ? (
        <form onSubmit={onSubmitHandler}>
          <BaseHeader
            leftSegment={
              <ArrowContainer aria-live="polite">
                <IconButton
                  aria-label="go to previous step"
                  icon={{
                    iconName: 'arrow-left',
                    size: '24px',
                    alt: 'back',
                    id: 'prev_step_footer',
                    color: 'text.secondary',
                  }}
                  onClick={
                    personalizedPlan.current === false
                      ? goBackWithReporter
                      : goBackToRecommended
                  }
                  disabled={isSubmitting}
                  type="button"
                  variant="bone"
                />
              </ArrowContainer>
            }
          />
          <RecommendedPlanContainer>
            <MobileHeader>
              <SummaryHeadline>{headline}</SummaryHeadline>
              <StarRating rating={5}>
                {reviewsCopy}
                <VisuallyHidden>{5} star rating for Ollie</VisuallyHidden>
              </StarRating>
            </MobileHeader>
            <ImageContainer>
              <ResponsiveImage
                priority={true}
                {...planImage}
                height={362}
                width={406}
              />
            </ImageContainer>
            <PlanInformation>
              <DesktopHeader>
                <SummaryHeadline>{headline}</SummaryHeadline>
                <StarRating rating={5}>
                  {reviewsCopy}
                  <VisuallyHidden>{5} star rating for Ollie</VisuallyHidden>
                </StarRating>
              </DesktopHeader>
              <RecipesText>
                {selectedRecipes.map(recipe => capitalize(recipe)).join(', ')}
              </RecipesText>
              <PricingInfo
                price={price}
                discountedPrice={discountedPrice}
                saveText={savePercentText}
                period={period}
              />
              <PupSummary>
                <List
                  items={pupInfo}
                  icon="checkmarkSimple"
                  gap={0}
                  typography="body"
                  iconColor="secondary.sea"
                />
              </PupSummary>
              <DesktopButtonContainer>
                <Button
                  type="submit"
                  aria-label={ctaButtonText}
                  disabled={isSubmitting}
                  variant="sea2"
                  isLoading={isSubmitting}
                  horizontalPadding={32}
                >
                  {ctaButtonText}
                </Button>
              </DesktopButtonContainer>
              <ButtonLink
                onClick={() => {
                  setShowRecommendedPlan(false);
                }}
              >
                {personalizePlanText}
              </ButtonLink>
              <MobileButtonContainer>
                {' '}
                <CheckoutBar {...checkoutBarProps} />
              </MobileButtonContainer>
            </PlanInformation>
          </RecommendedPlanContainer>
        </form>
      ) : (
        <RecommendedMealPlanForm
          {...mealPlanProps}
          onBackButtonClick={goBackToRecommended}
          isWithinOptimized={true}
        />
      )}
    </StyledRecommendedContainer>
  );
};
RecommendedPlanFormOptimized.displayName = 'RecommendedPlanFormOptimized';
RecommendedPlanFormOptimized.propTypes = {
  onRecommendedSubmit: PropTypes.func,
  headline: PropTypes.string,
  onBackButtonClick: PropTypes.func,
  reviewsCopy: PropTypes.string,
  savePercentText: PropTypes.string,
  defaultRecipes: PropTypes.array,
  getConfigFromRecipes: PropTypes.func,
  planImage: PropTypes.object,
  pupInfo: PropTypes.object,
  ctaButtonText: PropTypes.string,
  mealPlanProps: PropTypes.object,
  personalizePlanText: PropTypes.string,
  period: PropTypes.string,
};
